import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import BeneficiaireDocuments from "../components/BeneficiaireDocuments";
import BeneficiaireInterventions from "../components/BeneficiaireInterventions";
import BeneficiaireTransactions from "../components/BeneficiaireTransactions";
import BeneficiaireComments from "../components/BeneficiaireComments";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ChevronLeft, Contact, Phone, FolderOpen, CalendarRange, Receipt, MessageCircle } from "lucide-react";
import StickyPageHeader from "../components/StickyPageHeader";

export default function DossierBeneficiaire() {
  const { id } = useParams();
  const { user } = useAuth();
  const { brands } = useBrands();
  const [b, setB] = useState(null);
  const [cat, setCat] = useState(null);
  const [topTab, setTopTab] = useState("documents");

  useEffect(() => {
    (async () => {
      try {
        const [bs, cs] = await Promise.all([
          api.get("/beneficiaries"),
          api.get("/beneficiary-categories"),
        ]);
        const found = (bs.data || []).find((x) => x.id === id);
        setB(found);
        if (found) setCat((cs.data || []).find((c) => c.id === found.category_id));
      } catch {
        // ignore
      }
    })();
  }, [id]);

  const canManage = user?.role === "admin" || user?.role === "manager" || user?.role === "gestionnaire";
  const bs = b ? brands.filter((x) => (b.brand_ids || []).includes(x.id)) : [];

  return (
    <div className="space-y-6" data-testid="dossier-beneficiaire-page">
      {b ? (
        <Tabs value={topTab} onValueChange={setTopTab} className="w-full">
          <StickyPageHeader testid="dossier-benef-sticky-header">
          <Link to="/contacts" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900" data-testid="back-to-contacts">
            <ChevronLeft size={14} /> Retour aux contacts &amp; bénéficiaires
          </Link>

          <div>
            <p className="label-uppercase mb-2">Dossier bénéficiaire</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900 flex items-center gap-2">
              <Contact size={26} className="text-[#D97706]" />
              {b?.denomination || "—"}
            </h1>
            <div className="flex flex-wrap gap-4 text-sm text-zinc-600 mt-2 font-mono">
              {cat && (
                <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-sm" style={{ backgroundColor: `${cat.color}20`, color: cat.color }}>
                  {cat.name}
                </span>
              )}
              {b?.phone_number && (
                <span className="inline-flex items-center gap-1"><Phone size={12} /> {b.phone_country_code} {b.phone_number}</span>
              )}
              {b?.email && <span>{b.email}</span>}
              {b?.country && <span>{b.country}</span>}
            </div>
            {bs.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {bs.map((x) => (
                  <span key={x.id} className="text-[11px] font-mono inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-zinc-50 border border-zinc-200">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: x.color }} />
                    {x.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-white border border-zinc-200 rounded-sm p-0 h-auto inline-flex w-max">
              <TabsTrigger
                value="documents"
                className="rounded-sm px-4 py-2 text-sm data-[state=active]:bg-zinc-100"
                data-testid="ben-tab-documents"
              >
                <FolderOpen size={14} className="mr-1.5" /> Documents
              </TabsTrigger>
              <TabsTrigger
                value="interventions"
                className="rounded-sm px-4 py-2 text-sm data-[state=active]:bg-zinc-100"
                data-testid="ben-tab-interventions"
              >
                <CalendarRange size={14} className="mr-1.5" /> Historique d&apos;interventions
              </TabsTrigger>
              <TabsTrigger
                value="transactions"
                className="rounded-sm px-4 py-2 text-sm data-[state=active]:bg-zinc-100"
                data-testid="ben-tab-transactions"
              >
                <Receipt size={14} className="mr-1.5" /> Historique des transactions
              </TabsTrigger>
              <TabsTrigger
                value="comments"
                className="rounded-sm px-4 py-2 text-sm data-[state=active]:bg-zinc-100"
                data-testid="ben-tab-comments"
              >
                <MessageCircle size={14} className="mr-1.5" /> Commentaires et précisions
              </TabsTrigger>
            </TabsList>
          </div>
          </StickyPageHeader>

          <TabsContent value="documents" className="mt-5">
            <BeneficiaireDocuments
              beneficiaryId={b.id}
              canUpload={canManage}
              canDelete={canManage}
            />
          </TabsContent>

          <TabsContent value="interventions" className="mt-5">
            <BeneficiaireInterventions beneficiaryId={b.id} />
          </TabsContent>

          <TabsContent value="transactions" className="mt-5">
            <BeneficiaireTransactions beneficiaryId={b.id} />
          </TabsContent>

          <TabsContent value="comments" className="mt-5">
            <BeneficiaireComments beneficiaryId={b.id} />
          </TabsContent>
        </Tabs>
      ) : null}
    </div>
  );
}
